import axios from "axios";
import { toast } from "react-toastify";
import {
  ValidationRule,
  ComparisonRule,
  CausalityRule,
  HistoricalRule,
  CreateValidationRuleRequest,
  CreateComparisonRuleRequest,
  CreateCausalityRuleRequest,
  CreateHistoricalRuleRequest,
  RulesJobInfo,
} from "../interfaces";

export const getValidationRules = async (): Promise<ValidationRule[]> => {
  try {
    const response = await axios.get<ValidationRule[]>(
      `/api/rules/validation-rules`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching validation rules:", error);
    const message = error.response?.data?.message || "Failed to load validation rules";
    toast.error(message);
    throw error;
  }
};

export const getComparisonRules = async (): Promise<ComparisonRule[]> => {
  try {
    const response = await axios.get<ComparisonRule[]>(
      `/api/rules/comparison-rules`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching comparison rules:", error);
    const message = error.response?.data?.message || "Failed to load comparison rules";
    toast.error(message);
    throw error;
  }
};

export const getHistoricalRules = async (): Promise<HistoricalRule[]> => {
  try {
    const response = await axios.get<HistoricalRule[]>(
      `/api/rules/historical-rules`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching historical rules:", error);
    const message = error.response?.data?.message || "Failed to load historical rules";
    toast.error(message);
    throw error;
  }
};

export const getCausalityRules = async (): Promise<CausalityRule[]> => {
  try {
    const response = await axios.get<CausalityRule[]>(
      `/api/rules/causality-rules`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching causality rules:", error);
    const message = error.response?.data?.message || "Failed to load causality rules";
    toast.error(message);
    throw error;
  }
};

export const postCausalityRule = async (
  rule: CreateCausalityRuleRequest
): Promise<CausalityRule> => {
  try {
    const response = await axios.post(`/api/rules/add-causality-rule`, rule);
    return response.data;
  } catch (error: any) {
    console.error("Error creating causality rule:", error);
    const message = error.response?.data?.message || "Failed to create causality rule";
    toast.error(message);
    throw error;
  }
};

export const postHistoricalRule = async (
  rule: CreateHistoricalRuleRequest
): Promise<HistoricalRule> => {
  try {
    const response = await axios.post(`/api/rules/add-historical-rule`, rule);
    return response.data;
  } catch (error: any) {
    console.error("Error creating historical rule:", error);
    const message = error.response?.data?.message || "Failed to create historical rule";
    toast.error(message);
    throw error;
  }
};

export const postValidationRule = async (
  rule: CreateValidationRuleRequest
): Promise<ValidationRule> => {
  try {
    const response = await axios.post(
      `/api/rules/add-validation-rule?SkipMissingData=true`,
      rule
    );
    return response.data;
  } catch (error: any) {
    console.error("Error creating validation rule:", error);
    const message = error.response?.data?.message || "Failed to create validation rule";
    toast.error(message);
    throw error;
  }
};

export const postComparisonRule = async (
  rule: CreateComparisonRuleRequest
): Promise<ComparisonRule> => {
  try {
    const response = await axios.post(`/api/rules/add-comparison-rule`, rule);
    return response.data;
  } catch (error: any) {
    console.error("Error creating comparison rule:", error);
    const message = error.response?.data?.message || "Failed to create comparison rule";
    toast.error(message);
    throw error;
  }
};

export const runAllRules = async (): Promise<void> => {
  try {
    await axios.post(`/api/rules/apply-all-rules`);
  } catch (error: any) {
    console.error("Error applying all rules:", error);
    const message = error.response?.data?.message || "Failed to apply all rules";
    toast.error(message);
    throw error;
  }
};

export const deleteValidationRule = async (id: string): Promise<void> => {
  try {
    await axios.delete(`/api/rules/delete-validation-rule/${id}`);
    toast.success("Validation rule deleted successfully");
  } catch (error: any) {
    console.error("Error deleting validation rule:", error);
    const message = error.response?.data?.message || "Failed to delete validation rule";
    toast.error(message);
    throw error;
  }
};

export const deleteComparisonRule = async (id: string): Promise<void> => {
  try {
    await axios.delete(`/api/rules/delete-comparison-rule/${id}`);
    toast.success("Comparison rule deleted successfully");
  } catch (error: any) {
    console.error("Error deleting comparison rule:", error);
    const message = error.response?.data?.message || "Failed to delete comparison rule";
    toast.error(message);
    throw error;
  }
};

export const deleteHistoricalRule = async (id: string): Promise<void> => {
  try {
    await axios.delete(`/api/rules/delete-historical-rule/${id}`);
    toast.success("Historical rule deleted successfully");
  } catch (error: any) {
    console.error("Error deleting historical rule:", error);
    const message = error.response?.data?.message || "Failed to delete historical rule";
    toast.error(message);
    throw error;
  }
};

export const deleteCausalityRule = async (id: string): Promise<void> => {
  try {
    await axios.delete(`/api/rules/delete-causality-rule/${id}`);
    toast.success("Causality rule deleted successfully");
  } catch (error: any) {
    console.error("Error deleting causality rule:", error);
    const message = error.response?.data?.message || "Failed to delete causality rule";
    toast.error(message);
    throw error;
  }
};

export const searchHistoricalRules = async (
  searchTerm: string,
  relatedVariablesPrefix: string
): Promise<HistoricalRule[]> => {
  try {
    const response = await axios.get<HistoricalRule[]>(
      `/api/rules/historical-rules`,
      {
        params: {
          variableName: searchTerm,
          relatedVariablesPrefix,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error searching historical rules:", error);
    const message = error.response?.data?.message || "Failed to search historical rules";
    toast.error(message);
    throw error;
  }
};

export const searchCausalityRules = async (
  searchTerm: string
): Promise<CausalityRule[]> => {
  try {
    const response = await axios.get<CausalityRule[]>(
      `/api/rules/causality-rules`,
      {
        params: {
          firstVariableName: searchTerm,
          secondVariableName: searchTerm,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error searching causality rules:", error);
    const message = error.response?.data?.message || "Failed to search causality rules";
    toast.error(message);
    throw error;
  }
};

export const searchValidationRules = async (
  searchTerm: string
): Promise<ValidationRule[]> => {
  try {
    const response = await axios.get<ValidationRule[]>(
      `/api/rules/validation-rules`,
      {
        params: { variableName: searchTerm },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error searching validation rules:", error);
    const message = error.response?.data?.message || "Failed to search validation rules";
    toast.error(message);
    throw error;
  }
};

export const searchComparisonRules = async (
  searchTerm: string
): Promise<ComparisonRule[]> => {
  try {
    const response = await axios.get<ComparisonRule[]>(
      `/api/rules/comparison-rules`,
      {
        params: {
          firstVariableName: searchTerm,
          secondVariableName: searchTerm,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error searching comparison rules:", error);
    const message = error.response?.data?.message || "Failed to search comparison rules";
    toast.error(message);
    throw error;
  }
};

export const getAllRules = async (): Promise<{
  ValidationRules: ValidationRule[];
  ComparisonRules: ComparisonRule[];
  CausalityRules: CausalityRule[];
  HistoricalRules: HistoricalRule[];
}> => {
  try {
    const response = await axios.get<{
      ValidationRules: ValidationRule[];
      ComparisonRules: ComparisonRule[];
      CausalityRules: CausalityRule[];
      HistoricalRules: HistoricalRule[];
    }>(`/api/rules/all-rules`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching all rules:", error);
    const message = error.response?.data?.message || "Failed to load all rules";
    toast.error(message);
    throw error;
  }
};

export const activateRule = async (id: string, type: string): Promise<void> => {
  try {
    await axios.put(`/api/rules/activate/${type}/${id}`);
    toast.success(`${type} rule activated successfully`);
  } catch (error: any) {
    console.error("Error activating rule:", error);
    const message = error.response?.data?.message || "Failed to activate rule";
    toast.error(message);
    throw error;
  }
};

export const deactivateRule = async (id: string, type: string): Promise<void> => {
  try {
    await axios.put(`/api/rules/deactivate/${type}/${id}`);
    toast.success(`${type} rule deactivated successfully`);
  } catch (error: any) {
    console.error("Error deactivating rule:", error);
    const message = error.response?.data?.message || "Failed to deactivate rule";
    toast.error(message);
    throw error;
  }
};

export const updateValidationRule = async (id: string, rule: ValidationRule) => {
  try {
    const ruleToSend = {
      ...rule,
      applicablePhaseIdsJson: JSON.stringify(rule.applicablePhaseIds),
    };
    await axios.put(`/api/rules/update-validation-rule/${id}`, ruleToSend);
    toast.success("Validation rule updated successfully");
  } catch (error: any) {
    console.error("Error updating validation rule:", error);
    const message = error.response?.data?.message || "Failed to update validation rule";
    toast.error(message);
    throw error;
  }
};

export const updateComparisonRule = async (id: string, rule: ComparisonRule) => {
  try {
    const ruleToSend = {
      ...rule,
      applicablePhaseIdsJson: JSON.stringify(rule.applicablePhaseIds),
    };
    await axios.put(`/api/rules/update-comparison-rule/${id}`, ruleToSend);
    toast.success("Comparison rule updated successfully");
  } catch (error: any) {
    console.error("Error updating comparison rule:", error);
    const message = error.response?.data?.message || "Failed to update comparison rule";
    toast.error(message);
    throw error;
  }
};

export const updateCausalityRule = async (id: string, rule: CausalityRule) => {
  try {
    const ruleToSend = {
      ...rule,
      applicablePhaseIdsJson: JSON.stringify(rule.applicablePhaseIds),
    };
    await axios.put(`/api/rules/update-causality-rule/${id}`, ruleToSend);
    toast.success("Causality rule updated successfully");
  } catch (error: any) {
    console.error("Error updating causality rule:", error);
    const message = error.response?.data?.message || "Failed to update causality rule";
    toast.error(message);
    throw error;
  }
};

export const updateHistoricalRule = async (id: string, rule: HistoricalRule) => {
  try {
    const ruleToSend = {
      ...rule,
      applicablePhaseIdsJson: JSON.stringify(rule.applicablePhaseIds),
    };
    await axios.put(`/api/rules/update-historical-rule/${id}`, ruleToSend);
    toast.success("Historical rule updated successfully");
  } catch (error: any) {
    console.error("Error updating historical rule:", error);
    const message = error.response?.data?.message || "Failed to update historical rule";
    toast.error(message);
    throw error;
  }
};

export const startRunAllRulesTask = async (): Promise<{ jobId: string }> => {
  try {
    const url = `/api/rules/apply-all-rules/task`;
    const res = await axios.post(url, {}, { validateStatus: (s) => s === 202 || s === 200 });
    return res.data;
  } catch (error: any) {
    console.error("Error starting rules task:", error);
    const message = error.response?.data?.message || "Failed to start rules task";
    toast.error(message);
    throw error;
  }
};

export const getRunAllRulesTask = async (jobId: string): Promise<RulesJobInfo> => {
  try {
    const res = await axios.get<RulesJobInfo>(`/api/rules/apply-all-rules/task/${jobId}`);
    return res.data;
  } catch (error: any) {
    console.error("Error fetching rules task status:", error);
    throw error;
  }
};

export const cancelRunAllRulesTask = async (jobId: string): Promise<void> => {
  try {
    await axios.delete(`/api/rules/apply-all-rules/task/${jobId}`);
    toast.success("Rules task cancelled successfully");
  } catch (error: any) {
    console.error("Error cancelling rules task:", error);
    const message = error.response?.data?.message || "Failed to cancel rules task";
    toast.error(message);
    throw error;
  }
};